import { useState } from 'react';
import { Check, ChevronDown, Plus } from 'lucide-react';
import type { Organization } from '@/types/organization';

function OrgIcon({ org, size = 'sm' }: { org: { logoUrl?: string; name: string }; size?: 'sm' | 'md' }) {
  const px = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  const textSize = size === 'sm' ? 'text-[10px]' : 'text-xs';
  if (org.logoUrl) {
    return <img src={org.logoUrl} alt="" className={`${px} rounded object-cover shrink-0`} />;
  }
  return (
    <div className={`flex items-center justify-center ${px} rounded bg-foreground/10 text-foreground ${textSize} font-semibold shrink-0`}>
      {org.name?.charAt(0)?.toUpperCase() || 'O'}
    </div>
  );
}
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useOrganization } from '@/hooks/useOrganization';
import { useSidebar } from '@/components/ui/sidebar';
import { CreateOrganizationDialog } from './CreateOrganizationDialog';

export function OrganizationSwitcher() {
  const [open, setOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const {
    currentOrganization,
    organizations,
    switchOrganization,
    isLoading,
    isAdmin,
  } = useOrganization();
  const { state } = useSidebar();

  const handleSelect = async (organizationId: string) => {
    if (organizationId !== currentOrganization?.id) {
      await switchOrganization(organizationId);
    }
    setOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm bg-muted/50 rounded-md animate-pulse">
        <div className="w-6 h-6 rounded bg-muted shrink-0" />
        <span>Loading...</span>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <OrgIcon org={{ name: 'Personal' }} />
        <span>Personal</span>
      </div>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            aria-label="Select organization"
            className={cn(
              'w-full justify-between px-2 hover:bg-transparent hover:text-foreground group-data-[collapsible=icon]:!px-1',
              !currentOrganization && 'text-muted-foreground'
            )}
          >
            <div className="flex items-center gap-2 truncate">
              <OrgIcon org={currentOrganization || { name: 'Select' }} />
              <span className="truncate font-medium text-sm text-foreground">
                {currentOrganization?.name || 'Select organization'}
              </span>
            </div>
            {state !== 'collapsed' && <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder="Search organizations..." />
            <CommandList>
              <CommandEmpty>No organizations found.</CommandEmpty>
              <CommandGroup heading="Organizations">
                {organizations.map((organization) => (
                  <CommandItem
                    key={organization.id}
                    value={organization.name}
                    onSelect={() => handleSelect(organization.id)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <OrgIcon org={organization} />
                      <span className="truncate">{organization.name}</span>
                    </div>
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4',
                        currentOrganization?.id === organization.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
              {isAdmin && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setOpen(false);
                        setCreateDialogOpen(true);
                      }}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Create Organization
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover >

      <CreateOrganizationDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => setCreateDialogOpen(false)}
      />
    </>
  );
}